import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { Block } from '../classes/Block';
import type { ApiBlock as ApiBlockType } from '../utils/apiTypes';
import { VideoMode, VideoSettings, type SecondsPerCard } from './VideoSettings';
import { t } from '@lingui/core/macro';
import { getApiUrl } from '../utils/api';
import { useProject } from '../contexts/ProjectContext';
import LogoBlockConfig from './LogoBlockConfig';
import { CardGroup, getBlockCardGroup } from '../utils/cardGroups';

interface HtmlPreviewProps {
  blocks: Block[];
  actualSelectedBlockIndex: number;
  videoSettings: VideoSettings;
  columnDefs: any[];
  onBlockChange: (blockIndex: number, updatedBlock: Block) => void;
  onPreviewFocusedBlock?: (blockIndex: number) => void;
  scrollToBlockCounter?: number;
  cardGroups?: CardGroup[];
}

type PreviewMode = 'single' | 'surrounding' | 'full';

type ApiBlock = ApiBlockType;

interface ApiPayload {
  video_settings: {
    background_color: string;
    fps_num: number;
    resolution: string;
    transparent: boolean;
    video_time: number;
    fonts: string | undefined;
    logos: string | undefined;
    mode: VideoMode;
    seconds_per_card: SecondsPerCard;
  };
  data: ApiBlock[];
}

// Map resolution string to a base numeric width used for preview calculations
const RESOLUTION_WIDTHS: Record<string, number> = {
  "HD": 1280,
  "2K": 2048,
  "UHD": 3840,
  "4K": 4096,
};

function computeVideoWidth(resolution?: string): number {
  if (!resolution) return 1920;
  return RESOLUTION_WIDTHS[resolution] ?? RESOLUTION_WIDTHS['HD'];
}

function computeVideoHeightFromAspect(width: number, aspect: string | undefined): number {
  if (!aspect || !aspect.includes(':')) return Math.round(width * (9 / 16));
  const [wStr, hStr] = aspect.split(':');
  const w = Number(wStr);
  const h = Number(hStr);
  if (!w || !h) return Math.round(width * (9 / 16));
  return Math.round(width * (h / w));
}

function getBlocksForSelectedCard(blocks: Block[], cardGroups: CardGroup[], selectedIndex: number): Block[] {
  if (selectedIndex < 0 || selectedIndex >= blocks.length) return [];
  const group: CardGroup | undefined = getBlockCardGroup(cardGroups, blocks[selectedIndex].id);
  if (!group) return [blocks[selectedIndex]];
  return blocks.filter((b: Block) => group.blockIds.includes(b.id));
}

const HtmlPreview: React.FC<HtmlPreviewProps> = ({
  blocks,
  actualSelectedBlockIndex,
  videoSettings,
  columnDefs,
  onBlockChange,
  onPreviewFocusedBlock,
  scrollToBlockCounter,
  cardGroups = [],
}) => {
  const { selectedProject } = useProject();
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [iframeHeight, setIframeHeight] = useState<number>(0);
  const previewContainerRef = useRef<HTMLDivElement | null>(null);
  const savedScrollRef = useRef<number>(0);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const savedIframeScrollRef = useRef<number>(0);
  const iframeScrollHandlerRef = useRef<((event: Event) => void) | null>(null);
  const programmaticScrollRef = useRef<boolean>(false);
  
  // Load initial values from localStorage
  const [previewModeState, setPreviewModeState] = useState<PreviewMode>(() => {
    const saved: string | null = localStorage.getItem('fintitles_previewMode');
    if (saved === 'single' || saved === 'surrounding' || saved === 'full') {
      return saved;
    }
    return 'surrounding';
  });

  const [zoom, setZoomState] = useState<number>(() => {
    const saved: string | null = localStorage.getItem('fintitles_zoom');
    return saved !== null ? parseFloat(saved) : 1;
  });

  // In cards mode, full-roller features (block-focus messaging, scroll sync) are irrelevant
  const effectivePreviewMode: PreviewMode = videoSettings.mode === 'cards' ? 'single' : previewModeState;

  // Wrapper functions that update state AND localStorage
  const setPreviewMode = (value: PreviewMode): void => {
    setPreviewModeState(value);
    localStorage.setItem('fintitles_previewMode', value);
  };

  const setZoom = (value: number): void => {
    setZoomState(value);
    localStorage.setItem('fintitles_zoom', value.toString());
  };

  // Compute original content height from video settings resolution string and aspect ratio
  const originalContentHeight: number = useMemo(() => {
    const aspect: string = videoSettings?.aspect_ratio || '16:9';
    const widthPx: number = computeVideoWidth(videoSettings?.resolution);
    return computeVideoHeightFromAspect(widthPx, aspect);
  }, [videoSettings]);

  // Handler to resize iframe based on its content
  const detachIframeScrollListener = useCallback(() => {
    const iframeWindow: Window | null = iframeRef.current?.contentWindow ?? null;
    if (iframeWindow && iframeScrollHandlerRef.current) {
      iframeWindow.removeEventListener('scroll', iframeScrollHandlerRef.current);
    }
    iframeScrollHandlerRef.current = null;
  }, []);

  const attachIframeScrollListener = useCallback(() => {
    const iframeWindow: Window | null = iframeRef.current?.contentWindow ?? null;
    if (!iframeWindow) {
      return;
    }
    const handler = (event: Event) => {
      const targetDoc = (event.target as Document) ?? iframeWindow.document;
      const scrollTop: number =
        (targetDoc?.documentElement?.scrollTop ?? 0) ||
        (targetDoc?.body?.scrollTop ?? 0) ||
        iframeWindow.scrollY ||
        0;
      savedIframeScrollRef.current = scrollTop;
    };
    iframeScrollHandlerRef.current = handler;
    iframeWindow.addEventListener('scroll', handler, { passive: true });
  }, []);

  useEffect(() => {
    return () => {
      detachIframeScrollListener();
    };
  }, [detachIframeScrollListener]);

  // Listen for block-focus messages from iframe (Full Roller only)
  useEffect(() => {
    if (effectivePreviewMode !== 'full') return;

    const handleMessage = (event: MessageEvent): void => {
      if (programmaticScrollRef.current) return;
      if (event.source !== iframeRef.current?.contentWindow) return;

      const msg = event.data as { type?: string; blockName?: string; blockIndex?: number } | null;
      if (!msg || msg.type !== 'preview:block-focus') return;

      const resolvedIndex: number = (typeof msg.blockName === 'string' && msg.blockName.trim() !== '')
        ? blocks.findIndex((b: Block) => b.name === msg.blockName)
        : (Number.isInteger(msg.blockIndex) ? msg.blockIndex! : -1);

      if (resolvedIndex >= 0 && resolvedIndex < blocks.length && resolvedIndex !== actualSelectedBlockIndex) {
        onPreviewFocusedBlock?.(resolvedIndex);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [actualSelectedBlockIndex, blocks, onPreviewFocusedBlock, effectivePreviewMode]);

  // Scroll iframe to selected block when user clicks a tab (Full Roller only)
  useEffect(() => {
    if (effectivePreviewMode !== 'full' || !scrollToBlockCounter) return;

    const iframeWin: Window | null = iframeRef.current?.contentWindow ?? null;
    const iframeDoc: Document | null = iframeRef.current?.contentDocument ?? null;
    if (!iframeWin || !iframeDoc) return;

    const targetElement: HTMLElement | null = iframeDoc.querySelector(
      `[data-block-index="${actualSelectedBlockIndex}"]`
    );
    if (!targetElement) return;

    programmaticScrollRef.current = true;
    const elementTop: number = targetElement.offsetTop;
    const elementHeight: number = targetElement.offsetHeight;
    const viewportHeight: number = iframeWin.innerHeight;
    iframeWin.scrollTo({ top: elementTop - (viewportHeight - elementHeight) / 2, behavior: 'auto' });

    const timerId: ReturnType<typeof setTimeout> = setTimeout(() => {
      programmaticScrollRef.current = false;
    }, 300);

    return () => {
      clearTimeout(timerId);
      programmaticScrollRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToBlockCounter]);

  const handleIframeLoad = useCallback((event: React.SyntheticEvent<HTMLIFrameElement>): void => {
    const iframe: HTMLIFrameElement = event.currentTarget;
    iframeRef.current = iframe;
    detachIframeScrollListener();
    attachIframeScrollListener();
    try {
      const iframeDoc: Document | null = iframe.contentDocument || iframe.contentWindow?.document || null;
      if (iframeDoc && iframeDoc.body) {
        // At least one full frame tall, capped at one frame (internal scroll for tall content)
        const contentHeight: number = iframeDoc.body.scrollHeight;
        const finalHeight: number = Math.max(Math.min(contentHeight, originalContentHeight), originalContentHeight);
        setIframeHeight(finalHeight);
        // Restore scroll position after content recalculates height
        requestAnimationFrame(() => {
          const container: HTMLDivElement | null = previewContainerRef.current;
          if (container) {
            const maxScroll: number = container.scrollHeight - container.clientHeight;
            const desiredScroll: number = Math.min(savedScrollRef.current, Math.max(0, maxScroll));
            container.scrollTo({ top: desiredScroll });
          }

          const iframeWindow: Window | null = iframeRef.current?.contentWindow ?? null;
          if (iframeWindow) {
            const docElement = iframeWindow.document?.documentElement;
            const body = iframeWindow.document?.body;
            const maxIframeScroll: number =
              Math.max(docElement?.scrollHeight ?? 0, body?.scrollHeight ?? 0) -
              (iframeRef.current?.clientHeight ?? 0);
            const desiredIframeScroll: number = Math.min(
              savedIframeScrollRef.current,
              Math.max(0, maxIframeScroll)
            );
            iframeWindow.scrollTo({ top: desiredIframeScroll });
          }
        });
      }
    } catch (e) {
      // If we can't access iframe content (CORS), use default height
      setIframeHeight(originalContentHeight);
    }
  }, [attachIframeScrollListener, detachIframeScrollListener, originalContentHeight]);

  const handleContainerScroll = useCallback((event: React.UIEvent<HTMLDivElement>): void => {
    savedScrollRef.current = event.currentTarget.scrollTop;
  }, []);

  const transformBlockToApiFormat = useCallback(
    (block: Block): ApiBlock => {
      const rows: string[][] = [];
      if (block.dataRows && block.dataRows.length > 0) {
        for (const dataRow of block.dataRows) {
          const rowValues: string[] = [];
          for (const colDef of columnDefs) {
            let value: string = String(dataRow[colDef.field] || '');
            
            // Special handling for logo blocks: ensure col2 is JSON encoded with scale key
            if (value && block.isLogoBlock && colDef.field === 'col2') {
              try {
                // If value is already a JSON string, do nothing
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _ = JSON.parse(value);
              }
              catch {
                const scaleValue: number = parseFloat(value);
                value = JSON.stringify({ scale: isNaN(scaleValue) ? 100 : scaleValue });
              }
              
            }
            rowValues.push(value);
          }
          rows.push([...rowValues]);
        }
      }
      return {
        name: block.name,
        properties: block.toPreviewApiProperties(),
        rows: rows,
      };
    },
    [columnDefs]
  );

  const resolveBlocksForPreview = useCallback((): Block[] => {
    if (!blocks || blocks.length === 0) {
      return [];
    }

    if (actualSelectedBlockIndex < 0 || actualSelectedBlockIndex >= blocks.length) {
      return [];
    }

    if (videoSettings.mode === 'cards') {
      return getBlocksForSelectedCard(blocks, cardGroups, actualSelectedBlockIndex);
    }

    if (previewModeState === 'full') {
      return blocks;
    }

    if (previewModeState === 'single') {
      return [blocks[actualSelectedBlockIndex]];
    }

    const surroundingBlocks: Block[] = [];
    if (actualSelectedBlockIndex > 0) {
      surroundingBlocks.push(blocks[actualSelectedBlockIndex - 1]);
    }
    surroundingBlocks.push(blocks[actualSelectedBlockIndex]);
    if (actualSelectedBlockIndex < blocks.length - 1) {
      surroundingBlocks.push(blocks[actualSelectedBlockIndex + 1]);
    }
    return surroundingBlocks;
  }, [blocks, actualSelectedBlockIndex, previewModeState, videoSettings.mode, cardGroups]);

  const blocksForPreview: Block[] = useMemo(() => resolveBlocksForPreview(), [resolveBlocksForPreview]);

  useEffect(() => {
    const generateHtml = async (blocks: Block[]) => {
      if (!blocks || blocks.length === 0) {
        setHtmlContent('');
        setError(null);
        setIframeHeight(0);
        return;
      }
      setLoading(true);
      setError(null);
      setIframeHeight(0);
      try {
        const apiBlocks: ApiBlock[] = blocks.map((block) => transformBlockToApiFormat(block));

        const payload: ApiPayload = {
          video_settings: {
            background_color: videoSettings.background_color,
            fps_num: videoSettings.fps_num,
            resolution: videoSettings.resolution,
            transparent: videoSettings.transparent,
            video_time: videoSettings.video_time,
            fonts: JSON.stringify(videoSettings.fonts),
            logos: JSON.stringify(videoSettings.logos),
            mode: videoSettings.mode,
            seconds_per_card: videoSettings.seconds_per_card,
          },
          data: apiBlocks,
        };
        const response = await fetch(`${getApiUrl()}/html-preview?projectId=${selectedProject!.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();

        if (result.status === 'OK') {
          setHtmlContent(result.html || '');
          setError(null);
        } else {
          setError(result.message || 'Unknown error occurred');
          setHtmlContent('');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate HTML preview');
        setHtmlContent('');
      } finally {
        setLoading(false);
      }
    };

    if (blocksForPreview.length > 0 && columnDefs.length > 0) {
      generateHtml(blocksForPreview);
    } else {
      setHtmlContent('');
      setError(null);
    }
  }, [blocksForPreview, videoSettings, columnDefs, transformBlockToApiFormat, selectedProject]);

  const currentBlock: Block | undefined = useMemo(() => {
    if (!blocks || actualSelectedBlockIndex < 0 || actualSelectedBlockIndex >= blocks.length) {
      return undefined;
    }
    return blocks[actualSelectedBlockIndex];
  }, [blocks, actualSelectedBlockIndex]);

  const shouldRenderLogoConfig: boolean = Boolean(currentBlock?.isLogoBlock);

  // Derived numeric width/height used only for the preview rendering
  const previewWidth = computeVideoWidth(videoSettings?.resolution);

  const previewOptions: { value: PreviewMode; label: string }[] = useMemo(() => ([
    { value: 'single', label: t`Single Block` },
    { value: 'surrounding', label: t`Surrounding Blocks` },
    { value: 'full', label: t`Full Roller` },
  ]), []);

  return (
    <div id="html-preview" className="html-preview">
      <div
        className="preview-header"
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <h3>
            {t`Credits Preview`}
          </h3>
          <div style={{ paddingBottom: '1vh' }} className="tooltip-wrapper">
            <span className="tooltip-icon">?</span>
            <span className="tooltip-text">
              {t`The preview doesn't show the full width of the video (even in fullscreen).`}
            </span>
          </div>
        </div>
      </div>

      {/* Controls OUTSIDE the preview container for better layout */}
      <div className="preview-controls">
        {videoSettings.mode !== 'cards' && (
          <label className="preview-mode-select" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span>{t`Preview Scope:`}</span>
            <Select
              value={previewModeState}
              onChange={(e: SelectChangeEvent<string>) => setPreviewMode(e.target.value as PreviewMode)}
              style={{ width: '200px', height: '36px', fontSize: '14px' }}
              sx={{
                '.MuiSelect-select': { textAlign: 'left', display: 'flex', alignItems: 'center', paddingLeft: '10px' },
                '.MuiSelect-icon': { color: '#007CB2' }
              }}
              MenuProps={{ PaperProps: { style: { fontSize: 14 } } }}
            >
              {previewOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </label>
        )}

        <label className="zoom-control" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <span>{t`Zoom:`}</span>
          <input
            type="range"
            min={0.05}
            max={1}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
          />
          <span className="zoom-percentage">{Math.round(zoom * 100)}%</span>
          <div className="tooltip-wrapper" style={{ marginLeft: 8 }}>
            <span className="tooltip-icon">?</span>
            <span className="tooltip-text" style={{ height: "70px" }}>
              {t`This only changes the preview zoom and does not affect the actual video dimensions.`}
            </span>
          </div>
        </label>
      </div>

      {loading && (
        <div className="preview-loading">
          <p>{t`Generating preview...`}</p>
        </div>
      )}

      {error && (
        <div className="preview-error">
          <p>{t`Error: ${error}`}</p>
        </div>
      )}

      {!loading && !error && (
        <div
          className="preview-container"
          style={{
            width: '100%',
            maxHeight: '84vh',
            overflow: 'auto',
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start'
          }}
          ref={previewContainerRef}
          onScroll={handleContainerScroll}
        >
          {htmlContent ? (
            <div
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: 'top center',
                width: `${previewWidth}px`,
                height: 'auto',
                // overflowY: 'hidden'
              }}
            >
              <iframe
                style={{
                  width: `${previewWidth}px`,
                  height: iframeHeight > 0 ? `${iframeHeight}px` : `${originalContentHeight}px`,
                  border: 'none',
                  pointerEvents: 'auto',
                  display: 'block',
                  backgroundColor: videoSettings.transparent ? 'transparent' : videoSettings.background_color,
                }}
                srcDoc={htmlContent}
                title="HTML Preview"
                onLoad={handleIframeLoad}
              />
            </div>
          ) : (
            <p>{t`No preview available. Please select a block with data.`}</p>
          )}
        </div>
      )}

      {/* Logo Block Configuration */}
      {shouldRenderLogoConfig && currentBlock && (
        <LogoBlockConfig
          block={currentBlock}
          blockIndex={actualSelectedBlockIndex}
          onBlockChange={(updatedBlock) => onBlockChange(actualSelectedBlockIndex, updatedBlock)}
        />
      )}
    </div>
  );
};

export default HtmlPreview;